// Fase 15A — Gestione Consulenti (punti 10-12, 172): creazione via invito,
// nessuna password manuale. Lista con organizzazione e assegnazioni.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserCheck, Plus, Copy, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const USER_STATUS_STYLE = {
  ACTIVE: "bg-green-50 text-green-800 border-green-300",
  INVITED: "bg-sky-50 text-sky-800 border-sky-300",
  SUSPENDED: "bg-red-50 text-red-800 border-red-300",
  DISABLED: "bg-slate-100 text-slate-500 border-slate-300",
  ARCHIVED: "bg-slate-100 text-slate-500 border-slate-300",
};

export default function PlatformConsultants() {
  const [data, setData] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", organization_id: "", mfa_required: false });
  const [lastLink, setLastLink] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, o] = await Promise.all([
        api.get("/admin/platform/consultants", { params: { search: search || undefined } }),
        api.get("/admin/platform/organizations"),
      ]);
      setData(c.data);
      setOrgs(o.data.items || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data: r } = await api.post("/admin/platform/consultants", form);
      const link = `${window.location.origin}${r.activation_link}`;
      setLastLink(link);
      toast.success("Invito consulente creato", {
        description: "Copia il link di attivazione e invialo al consulente." });
      setForm({ full_name: "", email: "", organization_id: "", mfa_required: false });
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const hardDelete = async () => {
    try {
      await api.delete(`/admin/platform/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.email} eliminato definitivamente`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="consultants-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="consultants-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="platform-consultants-page">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
        <UserCheck className="w-7 h-7 text-green-800" aria-hidden="true" /> Consulenti
      </h1>

      <Card className="border-slate-200" data-testid="consultant-create-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <Plus className="w-4 h-4" aria-hidden="true" /> Nuovo Consulente
        </CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label htmlFor="cons-name">Nome e cognome *</Label>
              <Input id="cons-name" required value={form.full_name} data-testid="consultant-name-input"
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cons-email">Email *</Label>
              <Input id="cons-email" type="email" required value={form.email} data-testid="consultant-email-input"
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Studio *</Label>
              <Select value={form.organization_id}
                onValueChange={(v) => setForm((f) => ({ ...f, organization_id: v }))}>
                <SelectTrigger data-testid="consultant-org-select"><SelectValue placeholder="Seleziona studio" /></SelectTrigger>
                <SelectContent>
                  {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="submit" className="bg-green-700 hover:bg-green-800"
                disabled={!form.full_name || !form.email || !form.organization_id}
                data-testid="consultant-create-submit">Crea e genera invito</Button>
            </div>
          </form>
          <p className="text-[11px] text-slate-500">
            Il consulente riceve un link di attivazione e imposta la propria password:
            nessuna password viene mai definita o salvata dall'amministratore.
          </p>
          {lastLink && (
            <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 p-2"
              data-testid="consultant-invite-link">
              <code className="text-xs text-green-900 truncate flex-1">{lastLink}</code>
              <Button size="sm" variant="outline" className="h-7"
                data-testid="consultant-copy-link"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(lastLink);
                    toast.success("Link copiato");
                  } catch {
                    toast.info("Copia manualmente il link selezionandolo");
                  }
                }}>
                <Copy className="w-3 h-3 mr-1" aria-hidden="true" /> Copia
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome o email…" className="w-64 h-9"
          data-testid="consultants-search-input" />
      </div>

      <div className="space-y-2" data-testid="consultants-list">
        {data.items.length === 0 && (
          <p className="text-sm text-slate-500">Nessun consulente trovato.</p>)}
        {data.items.map((u) => (
          <div key={u.id} className="rounded-lg border border-slate-200 p-3 flex flex-wrap items-center gap-3"
            data-testid={`consultant-${u.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">{u.full_name}</p>
              <p className="text-[11px] text-slate-500">
                {u.email} · {u.organization_name || "nessuno studio"} · {u.assignments_count} aziende assegnate
              </p>
            </div>
            <Badge variant="outline" className={USER_STATUS_STYLE[u.status || "ACTIVE"]}
              data-testid={`consultant-status-${u.id}`}>{u.status || "ACTIVE"}</Badge>
            <Link to={`/admin/platform/utenti/${u.id}`}
              className="text-xs text-green-700 hover:underline"
              data-testid={`consultant-detail-${u.id}`}>Dettaglio →</Link>
            {!u.is_superadmin && (
              <Button size="sm" variant="outline"
                className="h-7 text-xs text-red-700 border-red-200 hover:bg-red-50"
                onClick={() => setDeleteTarget(u)}
                data-testid={`consultant-delete-${u.id}`}>
                <Trash2 className="w-3 h-3" aria-hidden="true" /></Button>
            )}
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="consultant-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente {deleteTarget?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati in modo irreversibile l'account ({deleteTarget?.email}),
              tutte le assegnazioni alle aziende e i dati collegati.
              L'operazione non è annullabile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="consultant-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-700 hover:bg-red-800" onClick={hardDelete}
              data-testid="consultant-delete-confirm">Elimina definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
